import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import authService from '../../services/authService';
import './ExecutionHistoryTab.css';

function taskDisplayName(task) {
  if (!task) return '';
  if (task.name) return task.name;
  const protocolNames = (task.protocols || []).map((p) => p.protocolName).filter(Boolean);
  return protocolNames.length > 0 ? protocolNames.join(', ') : '';
}

function taskLocationDisplay(task) {
  if (!task?.context) return '';
  return task.context.roomName || task.context.departmentName || task.context.locationName || '';
}

const MAX_DURATION_DISPLAY_MIN = 24 * 60; // 24h – cap bogus durations from wrong actualStart
function formatDuration(minutes) {
  const m = minutes ?? 0;
  if (m > MAX_DURATION_DISPLAY_MIN) return '> 24h';
  return `${m} min`;
}

/**
 * History tab: completed tasks for current user (PRD: execution_account_prd).
 * Uses auth uid for the query so Firestore rules (staffId == request.auth.uid) allow list.
 * Resolves task name and location from task_instances when completion doc lacks them (e.g. old data).
 */
export default function ExecutionHistoryTab({ userId }) {
  const { t } = useTranslation(['execution', 'tasks']);
  const [completions, setCompletions] = useState([]);
  const [taskMeta, setTaskMeta] = useState({}); // taskId -> { taskName, locationName }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const uid = authService.getCurrentUser()?.uid ?? userId;

  useEffect(() => {
    if (!uid) {
      setCompletions([]);
      setTaskMeta({});
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const q = query(
          collection(db, 'task_completions'),
          where('staffId', '==', uid),
          orderBy('completedAt', 'desc'),
          limit(50),
        );
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCompletions(list);

        const needResolution = list.filter((c) => !c.taskName || !c.locationName);
        const uniqueTaskIds = [...new Set(needResolution.map((c) => c.taskId).filter(Boolean))];
        if (uniqueTaskIds.length === 0) {
          setTaskMeta({});
          return;
        }
        const meta = {};
        const isPermissionDenied = (e) => e?.code === 'permission-denied' || e?.code === 'permission_denied';
        await Promise.all(
          uniqueTaskIds.map(async (taskId) => {
            try {
              const taskSnap = await getDoc(doc(db, 'task_instances', taskId));
              if (!taskSnap.exists()) return;
              const task = taskSnap.data();
              let taskName = taskDisplayName(task);
              if (!taskName) {
                const protocolId = task.protocols?.[0]?.protocolId || task.context?.protocolId;
                if (protocolId) {
                  try {
                    const protocolSnap = await getDoc(doc(db, 'protocols', protocolId));
                    if (protocolSnap.exists()) {
                      const protocol = protocolSnap.data();
                      taskName = protocol.name || '';
                    }
                  } catch (e) {
                    if (process.env.NODE_ENV === 'development' && !isPermissionDenied(e)) {
                      console.warn('ExecutionHistoryTab resolve protocol', protocolId, e);
                    }
                  }
                }
              }
              meta[taskId] = {
                taskName: taskName || taskId,
                locationName: taskLocationDisplay(task),
              };
            } catch (err) {
              if (process.env.NODE_ENV === 'development' && !isPermissionDenied(err)) {
                console.warn('ExecutionHistoryTab resolve task', taskId, err);
              }
              meta[taskId] = { taskName: taskId, locationName: '' };
            }
          }),
        );
        setTaskMeta(meta);
      } catch (e) {
        if (process.env.NODE_ENV === 'development' && e?.code !== 'permission-denied') {
          console.warn('ExecutionHistoryTab load error', e);
        }
        setError(e.message);
        setCompletions([]);
        setTaskMeta({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [uid]);

  const formatTime = (ts) => {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (ts) => {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleDateString();
  };

  if (!uid) return null;

  if (loading) {
    return (
      <div className="execution-history-tab">
        <h2>{t('taskHistory')}</h2>
        <p className="execution-history-loading">{t('tasks:loadingTasks', 'Se încarcă...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="execution-history-tab">
        <h2>{t('taskHistory')}</h2>
        <p className="execution-history-error">{error}</p>
      </div>
    );
  }

  const byDate = completions.reduce((acc, c) => {
    const key = formatDate(c.completedAt) || 'unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort((a, b) => {
    const dA = byDate[a][0]?.completedAt?.toMillis?.() ?? 0;
    const dB = byDate[b][0]?.completedAt?.toMillis?.() ?? 0;
    return dB - dA;
  });

  return (
    <div className="execution-history-tab">
      <h2>{t('taskHistory')}</h2>
      {completions.length === 0 ? (
        <p className="execution-history-empty">{t('noCompletionsYet', 'Nicio sarcină finalizată încă.')}</p>
      ) : (
        <div className="execution-history-list">
          {dates.map((dateKey) => (
            <div key={dateKey} className="execution-history-group">
              <h3 className="execution-history-date">{dateKey}</h3>
              {byDate[dateKey].map((c) => (
                <div key={c.id} className="execution-history-card">
                  <span className="execution-history-card-risk" />
                  <div className="execution-history-card-body">
                    <strong>{c.taskName || taskMeta[c.taskId]?.taskName || c.protocolId || c.taskId}</strong>
                    <span className="execution-history-location">{c.locationName || taskMeta[c.taskId]?.locationName || '—'}</span>
                    <span className="execution-history-time">
                      {formatTime(c.completedAt)} · {formatDuration(c.actualDuration)}
                    </span>
                    <span className="execution-history-card-icons">
                      {c.deviations?.length > 0 && (
                        <span className="execution-history-deviations">
                          ⚠️ {c.deviations.length}
                        </span>
                      )}
                      <span className="execution-history-check">✓</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
